import React, { useState, useRef } from 'react'
import DOMPurify from 'dompurify'
import { GridColumn, Header, Popup, Grid } from 'semantic-ui-react'
import Loading from 'components/Loading'
import _ from 'lodash'
import { useTheme } from 'contexts/ThemeProvider'
import themeStyles from './styles'
import NoData from './NoData'
import localization from './localization'
import withLocalization from '@web/helpers/localizationUtils'
import Text from '@web/containers/MyHealth/components/Text'
import useMediaQuery from 'hooks/useMediaQuery'
import { useSelector } from 'react-redux'
import { healthLibraryError } from '@web/stores/FhirEnhanced/Select'

const LearnMoreButton = ({
  data,
  onClick,
  onClose,
  shouldLoading,
  buttonStyle,
  mapKey,
  language,
  t
}) => {
  const { theme, isDark } = useTheme()
  const styles = themeStyles(theme)
  const { isMobile } = useMediaQuery()
  const allDataEmpty = _.isEmpty(data)
  const combinedButtonStyle = () => ({
    ...styles.triggerButton(isMobile),
    ...buttonStyle
  })
  const hasError = _.get(useSelector(healthLibraryError), mapKey, null)
  const [clickedPosition, setClickedPosition] = useState('top left')
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef(null)
  const isSpanish = language === 'es'

  const handleClickTrigger = event => {
    const screenHeight = window.innerHeight
    const screenWidth = window.innerWidth
    const clickPosition = event.clientY
    const rightClientX = event.target.getBoundingClientRect().right
    const leftClientX = event.target.getBoundingClientRect().left
    let positionY = ''
    let positionX = ''
    if (clickPosition < screenHeight / 2) {
      positionY = 'bottom'
    } else {
      positionY = 'top'
    }
    if (rightClientX > (screenWidth * 3) / 4) {
      positionX = 'right'
    } else if (
      rightClientX > screenWidth / 2 &&
      leftClientX > screenWidth / 4
    ) {
      positionX = 'center'
    } else {
      positionX = 'left'
    }
    setClickedPosition(`${positionY} ${positionX}`)
    setIsOpen(true)
    if (onClick) {
      onClick()
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <span
        ref={triggerRef}
        style={combinedButtonStyle(isMobile)}
        onClick={e => handleClickTrigger(e)}
      >
        {t('learnMore')}
      </span>
      <Popup
        context={triggerRef}
        open={isOpen}
        onClose={handleClose}
        style={styles.popupModal(isMobile, isDark, clickedPosition)}
        position={clickedPosition}
        pinned
      >
        {shouldLoading && allDataEmpty && <Loading />}
        {hasError && !shouldLoading && allDataEmpty && (
          <div style={styles.errorContainer}>
            <p style={styles.errorText}>{t('error')}</p>
          </div>
        )}
        {!allDataEmpty ? (
          <Grid
            centered
            divided
            columns={1}
            style={styles.popupContent(isMobile)}
          >
            {data.map((item, index) => {
              const regularTitle = _.get(item, 'regularTitle', '')
              const nativeLanguageTitle = _.get(item, 'nativeLanguageTitle', '')
              const blurb = _.get(item, 'blurb', '')
              const body = _.get(item, 'body', '')
              const noData = !regularTitle && !nativeLanguageTitle && !blurb
              const title = nativeLanguageTitle || regularTitle
              // eslint-disable-next-line no-undef
              const domParser = new DOMParser()
              const blurbHTML = domParser.parseFromString(
                blurb || '',
                'text/html'
              ).documentElement.textContent
              const sanitizedBlurbHTML = DOMPurify.sanitize(blurbHTML)
              return !noData ? (
                <GridColumn key={index}>
                  <Header
                    as={body ? 'a' : 'h1'}
                    href={`/healthArticles?key=${encodeURIComponent(mapKey)}&index=${index}`}
                    target={'_blank'}
                    rel='noopener noreferrer'
                  >
                    <Text
                      noPadding
                      text={title}
                      textStyle={styles.title(body)}
                    />
                  </Header>
                  {blurb && !isSpanish && (
                    <div
                      style={styles.blurbContainer}
                      dangerouslySetInnerHTML={{ __html: sanitizedBlurbHTML }}
                    />
                  )}
                </GridColumn>
              ) : null
            })}
          </Grid>
        ) : (
          !shouldLoading && !hasError && <NoData />
        )}
      </Popup>
    </>
  )
}

export default withLocalization(LearnMoreButton, localization)
